import { ClientAccount } from 'api/Serializers/Clients';
import { PaymentMethodSerializer } from 'api/Serializers/PaymentMethods';
import Button from 'components/button';
import Callout from 'components/callout';
import Controls from 'components/controls';
import CreditCardDetails from 'components/credit-card-details';
import Link from 'components/link';
import Loading from 'components/loading';
import Modal from 'components/modal';
import {
  APP_ROOT_URL,
  CANCEL_POLICY_HRS,
  DATE_FMT,
  PAYMENT_AUTH_DAYS,
  PAYMENT_SETTLEMENT_HOURS,
} from 'config';
import PaymentMethodSelect from 'containers/payment-select';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { MoneyIcon } from 'icons';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getAccountCredit,
  getAccountDetail,
  getAppointments,
  getCartAppointmentProduct,
  getCartError,
  getCartMultiAvailabilityList,
  getCartPaymentMethod,
  getCartSingleAvailability,
  getCartStatus,
  getUsername,
} from 'state/selectors';
import { fetchAccountCredit } from 'state/slice/account';
import { fetchClientAppointments } from 'state/slice/appointments';
import { checkout, setCartPaymentMethod } from 'state/slice/cart';
import { APP_ROUTES, SHARED_ROUTES } from 'utils/routing';
import {
  AppointmentProductPriceDetails,
  BookingHeader,
  Divider,
} from '../components';
import { BookingError, PaymentMethodError, StepProps } from '../types';
import { getCanEditCart, getStepLink, toLocalTime } from '../utils';

const AccountCredit = () => {
  const credit = useSelector(getAccountCredit);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchAccountCredit());
  }, []);
  if (!credit || credit < 1) {
    return null;
  }
  return (
    <div>
      <BookingHeader title="Account credit" />
      <div className="flex space-x-2">
        <MoneyIcon width={20} className="text-green-600" />
        <span className="text-base font-medium">{credit?.toCurrency()}</span>
        <span className="text-sm italic text-gray-600">
          Automatically applied at booking
        </span>
      </div>
    </div>
  );
};

const PolicyAgreements = () => {
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const appointmentProduct = useSelector(getCartAppointmentProduct);
  const selectedAvailability = useSelector(getCartSingleAvailability);

  if (selectedAvailability === undefined) {
    // HACK: fixes bug that's been popping up, but dont know how it's possible
    return null;
  }

  const hasSpacePolicy = appointmentProduct.facility.category === 'Hotel';
  const cancellationDateTime = moment(selectedAvailability.start)
    .tz(appointmentProduct.timezone)
    .subtract(CANCEL_POLICY_HRS, 'hours');
  const now = moment();
  const authDt = moment(selectedAvailability.start)
    .tz(appointmentProduct.timezone)
    .subtract(PAYMENT_AUTH_DAYS * 24, 'hours');
  const outsideAuthWindow = now.isBefore(authDt);
  const isLastMinuteBooking = moment(selectedAvailability.created).isAfter(
    moment(selectedAvailability.start).subtract(48, 'hours')
  );
  return (
    <>
      <Divider />
      {!isLastMinuteBooking ? (
        <div>
          <h3 className="my-0 font-semibold text-md">Book now, pay later</h3>
          <p>
            {outsideAuthWindow
              ? `Your card will be pre-authorized ${PAYMENT_AUTH_DAYS} days prior to the ${appointmentProduct.activity.appointmentNoun.toLowerCase()}, then charged ${PAYMENT_SETTLEMENT_HOURS} hours after.`
              : `Your card will be pre-authorized immediately, then charged ${PAYMENT_SETTLEMENT_HOURS} hours after the ${appointmentProduct.activity.appointmentNoun.toLowerCase()}.`}
          </p>
          <p>
            Any account credit will be used immediately towards the total. We'll
            also check for credit before your card is charged.
          </p>
        </div>
      ) : (
        <div>
          <h3 className="my-0 font-semibold text-md">Payment</h3>
          <p>
            Your card will be immediately charged. Existing account credit will
            be applied before a card charge is made.
          </p>
        </div>
      )}
      <div className="space-y-6">
        {!isLastMinuteBooking ? (
          <div>
            <BookingHeader
              title={`${CANCEL_POLICY_HRS}-hour cancellation policy`}
            />
            <p>
              <strong>
                Free cancellation before{' '}
                {cancellationDateTime.format(DATE_FMT.TIME_A)} on{' '}
                {cancellationDateTime.format(DATE_FMT.MONTH_D)}
              </strong>
              . After that, the{' '}
              {appointmentProduct.activity.appointmentNoun.toLowerCase()} is
              non-refundable.
            </p>
          </div>
        ) : (
          <div>
            <BookingHeader title="Cancellation policy" />
            <p>
              Bookings confirmed within 48 hours of the lesson start time are
              final. If you cancel, this lesson will not be eligible for a
              refund.
            </p>
          </div>
        )}
        {hasSpacePolicy && (
          <Modal
            name="Checkout — Pool space policy"
            open={showPolicyModal}
            onClose={() => setShowPolicyModal(false)}
            title="Pool space policy"
            maxWidth="sm"
          >
            <p>
              In the rare event that hotel guests and swimmers cannot both use
              the space without obstructing each other, the instructor must
              yield and cancel the lesson. You will not be charged, your
              instructor will be compensated, and Propel provide you with a $20
              credit for the inconvenience.
            </p>
          </Modal>
        )}
        <div>
          <p className="text-xs">
            By selecting the button below, I agree to the{' '}
            {!isLastMinuteBooking ? `${CANCEL_POLICY_HRS}-hour ` : ''}
            Cancellation Policy
            {hasSpacePolicy && (
              <>
                {' and the '}
                <span className="link" onClick={() => setShowPolicyModal(true)}>
                  Pool Space Policy
                </span>
              </>
            )}
            . I also agree to Propel's{' '}
            <Link to={APP_ROUTES.PRIVACY} target="_blank">
              Privacy Policy
            </Link>
            {' and '}
            <Link to={APP_ROUTES.TERMS_OF_SERVICE} target="_blank">
              Terms of Service
            </Link>
            .
          </p>
        </div>
      </div>
    </>
  );
};

const SelectPaymentMethodAndBookNow = ({
  state,
  onClickContinue,
  onClickEdit,
  bookingType,
}: StepProps<BookingError>) => {
  if (state === 'hidden') {
    return null;
  }
  const dispatch = useAppDispatch();
  const history = useHistory();
  const username = useSelector(getUsername);
  const appointments = useSelector(getAppointments);
  const account = useSelector(getAccountDetail) as ClientAccount;
  const paymentMethods = account?.paymentMethods || [];
  const appointmentProduct = useSelector(getCartAppointmentProduct); // This is cart's appointmentProduct
  const singleAvailability = useSelector(getCartSingleAvailability);
  const multiAvailabilityList = useSelector(getCartMultiAvailabilityList);
  const selectedPaymentMethod = useSelector(getCartPaymentMethod);
  const cartStatus = useSelector(getCartStatus);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const cartError = useSelector(getCartError);
  const [error, setError] = useState<PaymentMethodError>(undefined);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const isLastMinuteBooking =
    bookingType === 'single' &&
    singleAvailability !== undefined &&
    moment(singleAvailability.created).isAfter(
      moment(singleAvailability.start).subtract(48, 'hours')
    );

  const sameDayAppt =
    bookingType === 'single'
      ? appointments.find(
          (appt) => appt.date === singleAvailability?.date && !appt.cancelled
        )
      : undefined;

  const handleBookNow = async () => {
    // By using the state variable isSubmitted with useEffect, we can guard
    // against double submissions between state transitions
    setIsSubmitted(true);
    await dispatch(checkout());
    let fetchRange: [string, string];
    if (bookingType === 'single') {
      fetchRange = [singleAvailability.date, singleAvailability.date];
    } else {
      fetchRange = [
        toLocalTime(
          multiAvailabilityList[0].start,
          appointmentProduct.timezone
        ).format(DATE_FMT.DATE_FIELD),
        toLocalTime(
          multiAvailabilityList[multiAvailabilityList.length - 1].start,
          appointmentProduct.timezone
        ).format(DATE_FMT.DATE_FIELD),
      ];
    }
    dispatch(
      fetchClientAppointments({
        start: fetchRange[0],
        end: fetchRange[1],
      })
    );
    setIsSubmitted(false);
  };

  const handleCardSelect = (paymentMethod: PaymentMethodSerializer) => {
    dispatch(setCartPaymentMethod(paymentMethod));
    onClickContinue();
    setError(undefined);
  };

  useEffect(() => {
    if (paymentMethods?.length > 0 && !selectedPaymentMethod) {
      let bestGuess = paymentMethods.find((pm) => pm.isDefault);
      if (!bestGuess) {
        bestGuess = paymentMethods.sort((a, b) =>
          a.created > b.created ? -1 : 1
        )[0];
      }
      if (bestGuess) {
        handleCardSelect(bestGuess);
      }
    } else if (selectedPaymentMethod !== undefined) {
      onClickContinue();
    }
  }, []);

  if (!username) {
    history.push(
      getStepLink('participants', bookingType, {
        appointmentProduct,
        selectedAvailability: singleAvailability,
      })
    );
    return <Loading />;
  }

  return (
    <>
      <Divider />
      <div className="space-y-6">
        <div className="md:hidden">
          <AppointmentProductPriceDetails />
        </div>
        <AccountCredit />
        <div>
          <BookingHeader
            title="Payment method"
            onEdit={state === 'editing' ? null : onClickEdit}
          />
          {state === 'editing' && (
            <div className="flex-1 max-w-sm">
              <p>A valid payment method is required for all bookings.</p>
              <PaymentMethodSelect
                stripeConfigReturnUrl={`${APP_ROOT_URL}${getStepLink(
                  'payment',
                  bookingType,
                  {
                    appointmentProduct,
                    selectedAvailability: singleAvailability,
                  }
                )}`}
                onSelect={handleCardSelect}
              />
              {error && (
                <div className="input-error-message">
                  Please select a payment method
                </div>
              )}
            </div>
          )}
        </div>
        {state !== 'editing' && (
          <>
            <CreditCardDetails
              paymentMethod={selectedPaymentMethod}
              showDefaultBadge={false}
            />
            {cartError?.error === 'payment_method_failed' && (
              <Callout type="error" title="Payment method failed">
                {cartError.message}
              </Callout>
            )}
            <PolicyAgreements />
          </>
        )}
      </div>
      {cartStatus === 'COMPLETE' ? (
        <Callout type="success" title="Booking complete!"></Callout>
      ) : (
        <>
          <Controls>
            <Button
              color="primary"
              size="large"
              variant="contained"
              onClick={() =>
                isLastMinuteBooking
                  ? setShowConfirmation(true)
                  : handleBookNow()
              }
              disabled={
                state !== 'complete' ||
                isSubmitted ||
                !getCanEditCart(cartStatus)
              }
            >
              Book now
            </Button>
          </Controls>
        </>
      )}
      <Modal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        title="Confirm booking?"
        name="non-refundable-confirmation"
        maxWidth="xs"
      >
        <p className="my-2">
          If you cancel, this lesson will not be eligible for a refund.
        </p>
        {sameDayAppt !== undefined && (
          <Callout className="my-4" type="warning">
            You have an existing booking on the same day at{' '}
            {moment(sameDayAppt.start).format(DATE_FMT.TIME_A)} which is no
            longer eligible for a refund.
            <div className="mt-4">
              <Link
                className="!underline !hover:underline" // HACK: Forcing the underline because the Link can't tell its child is a string
                to={SHARED_ROUTES.SCHEDULE.appointment(sameDayAppt.id)}
                target="_blank"
              >
                View existing appointment on{' '}
                {moment(sameDayAppt.start).format(DATE_FMT.MON_D_ORDINAL)}
              </Link>
            </div>
          </Callout>
        )}

        <Controls className="!pb-0">
          <Button
            variant="flat"
            color="primary"
            onClick={() => {
              setShowConfirmation(false);
              handleBookNow();
            }}
          >
            Confirm
          </Button>
          <Button variant="flat" onClick={() => setShowConfirmation(false)}>
            Cancel
          </Button>
        </Controls>
      </Modal>
    </>
  );
};

export default SelectPaymentMethodAndBookNow;
